import React, { useState } from 'react';
import { checkForUpdates, getCurrentVersion, clearVersionCache } from '../utils/versionCheck';
import { assignPlayersToTeams } from '../utils/teamAssignment';
import { createBalancedMatchSchedule } from '../utils/matchAlgorithm';
import { buildTeammateMatrix, summarizeTeammates, buildFieldUsage, summarizeFieldUsage } from '../utils/scheduleMetrics';

const VersionDebugPanel = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [simResult, setSimResult] = useState(null);

  const handleManualCheck = async () => {
    setIsChecking(true);
    setCheckResult(null);
    
    try {
      const hasUpdate = await checkForUpdates((currentVersion, latestVersion) => {
        setCheckResult(`Update found: ${currentVersion} → ${latestVersion}`);
      });
      
      if (!hasUpdate) {
        setCheckResult('No update available');
      }
    } catch (error) {
      setCheckResult(`Check failed: ${error.message}`);
    } finally {
      setIsChecking(false);
    }
  };
  const handleClearCache = () => {
    clearVersionCache();
    // Also clear any lingering localStorage entries
    localStorage.removeItem('last_build_timestamp');
    setCheckResult('Version cache cleared');
  };

  const runSimulation = () => {
    const players = Array.from({ length: 20 }, (_, i) => `P${i + 1}`);
    const numTeams = 6 * 2; // 12 teams (not realistic for 20 players); adapt to players
    // With 20 players, choose teams such that average size ~ 3-4; pick 6 teams for demo
    const teamsCount = 6; // 6 teams => avg ~3-4
    const rounds = 6; // try a few rounds
    const numFields = 6;
    const history = [];
    for (let r = 0; r < rounds; r++) {
      const roundTeams = assignPlayersToTeams(players, teamsCount, history, null);
      history.push(roundTeams);
    }
    // Build a display matrix like the app does
    const tournamentMatrix = history.map((roundTeams, roundIndex) => {
      const playerToTeamMap = {};
      roundTeams.forEach((team, teamIndex) => {
        team.forEach(playerName => {
          playerToTeamMap[playerName] = String.fromCharCode(65 + teamIndex);
        });
      });
      return {
        round: roundIndex + 1,
        assignments: players.map(playerName => ({ playerName, team: playerToTeamMap[playerName] || '-' }))
      };
    });

    const schedule = createBalancedMatchSchedule(history, numFields);

    const getTeamField = (roundIdx, teamLetter) => {
      const teamIndex = teamLetter.charCodeAt(0) - 65;
      const match = schedule[roundIdx]?.find(m => m.teamA === teamIndex || m.teamB === teamIndex);
      return match ? match.field : null;
    };

    const { matrix } = buildTeammateMatrix(players, tournamentMatrix);
    const mateStats = summarizeTeammates(players, matrix);
    const fieldUsage = buildFieldUsage(players, tournamentMatrix, getTeamField);
    const fieldStats = summarizeFieldUsage(fieldUsage);

    setSimResult({ mateStats, fieldStats });
  };

  if (!isVisible) {
    return (
      <button
        onClick={() => setIsVisible(true)}
        style={{
          position: 'fixed',
          bottom: '10px',
          left: '10px',
          padding: '5px 10px',
          background: '#666',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          fontSize: '12px',
          cursor: 'pointer',
          zIndex: 1000
        }}
      >
        Debug
      </button>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: '10px',
      left: '10px',
      background: 'white',
      border: '1px solid #ccc',
      borderRadius: '8px',
      padding: '15px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: 1000,
      fontSize: '14px',
      maxWidth: '320px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h4 style={{ margin: 0 }}>Debug</h4>
        <button onClick={() => setIsVisible(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
      </div>

      <p style={{ margin: '5px 0' }}>Current Version: <strong>{getCurrentVersion()}</strong></p>

      <div style={{ display: 'flex', gap: '5px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <button
          onClick={handleManualCheck}
          disabled={isChecking}
          style={{ padding: '5px 10px', background: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: isChecking ? 'not-allowed' : 'pointer', fontSize: '12px' }}
        >
          {isChecking ? 'Checking...' : 'Check Now'}
        </button>
        <button
          onClick={handleClearCache}
          style={{ padding: '5px 10px', background: '#f44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
        >
          Clear Cache
        </button>
        <button
          onClick={runSimulation}
          style={{ padding: '5px 10px', background: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
        >
          Simulate 20P / 6F
        </button>
      </div>

      {checkResult && (
        <div style={{ padding: '8px', background: '#f5f5f5', borderRadius: '4px', fontSize: '12px', wordBreak: 'break-word', marginBottom: '8px' }}>
          {checkResult}
        </div>
      )}

      {simResult && (
        <div style={{ padding: '8px', background: '#f0faf0', borderRadius: '4px', fontSize: '12px', wordBreak: 'break-word' }}>
          <div><strong>Teammates</strong></div>
          <div>Min unique teammates: {simResult.mateStats.minUniqueTeammates}</div>
          <div>Max repeats w/ same teammate: {simResult.mateStats.maxRepeatWithSameTeammate}</div>
          <div style={{ marginTop: '6px' }}><strong>Fields</strong></div>
          <div>Max field spread: {simResult.fieldStats.maxSpread}</div>
        </div>
      )}
    </div>
  );
};

export default VersionDebugPanel;
